import { v4 as uuidv4 } from 'uuid';

import { getLocalStorageItem } from '../getLocalStorageItem';
import { ELocalStorageVariables } from '../localStorageTypes';
import { setLocalStorageItem } from '../setLocalStorageItem';

// The window is sliding, not absolute. It was previously set once at mint and
// never extended, which split a continuous journey at an arbitrary wall-clock
// point: an applicant working for four hours and ten minutes became two
// sessions, and the first looked like an abandonment that never happened. Since
// drop-off is the metric this data exists to produce, an absolute expiry
// manufactures false positives in exactly the number that matters.
// Exported because the tracking module's inactivity end-of-session has to fire
// just inside this window — a second copy of the number would let the two
// drift and the end event would attach to the wrong session.
export const INACTIVITY_WINDOW_MS = 1000 * 60 * 30;

// A hard ceiling so a session cannot run indefinitely on a page left open.
const ABSOLUTE_CAP_MS = 1000 * 60 * 60 * 12;

type SessionData = {
    uuid: string;
    expirationTimestamp: number; // Sliding: refreshed on every read
    startedAt?: number; // Fixed at mint, enforces the cap
    seq?: number; // Monotonic event counter, see nextSequence
};

function mint(): string {
    const now = Date.now();
    const newUuid = uuidv4();

    write({
        uuid: newUuid,
        expirationTimestamp: now + INACTIVITY_WINDOW_MS,
        startedAt: now,
        seq: 0,
    });

    return newUuid;
}

function write(data: SessionData): void {
    setLocalStorageItem(ELocalStorageVariables.SESSION_ID, JSON.stringify(data));
}

function read(): SessionData | null {
    const raw = getLocalStorageItem(ELocalStorageVariables.SESSION_ID);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        if (!parsed?.uuid) return null;
        return parsed as SessionData;
    } catch (error) {
        return null;
    }
}

export function getSessionId(): string {
    const session = read();
    if (!session) return mint();

    const now = Date.now();

    // Idle too long, so the previous session is over.
    if (now >= session.expirationTimestamp) return mint();

    // Older sessions were written without startedAt; treat the current read as
    // the start rather than expiring every existing session on deploy.
    const startedAt = session.startedAt ?? now;
    if (now - startedAt >= ABSOLUTE_CAP_MS) return mint();

    // Activity extends the window. seq is carried through deliberately: this runs
    // on every recorded event, so dropping it would reset the counter each time
    // and every event would be numbered 1, leaving the path unorderable.
    write({
        uuid: session.uuid,
        expirationTimestamp: now + INACTIVITY_WINDOW_MS,
        startedAt,
        seq: session.seq,
    });

    return session.uuid;
}

// A monotonic counter, unique and increasing within a session, stamped on every
// event as it is created.
//
// This is what makes the path reconstructable. The server timestamps events on
// receipt, which is right for wall-clock time but useless for ordering here:
// events are sent in batches, so a whole batch shares one timestamp, and batches
// can arrive out of order. Sorting by timestamp would shuffle the steps within a
// batch and could invert them across batches. Sorting by this cannot.
//
// It lives beside the session id in localStorage so it survives a reload, and
// resets to zero whenever a new session is minted.
export function nextSequence(): number {
    const session = read();
    if (!session) {
        // No session yet. Minting one resets the counter, so this event is the
        // first of the new session.
        getSessionId();
        const minted = read();
        if (minted) write({ ...minted, seq: 1 });
        return 1;
    }

    const seq = (session.seq ?? 0) + 1;
    write({ ...session, seq });

    return seq;
}

// True when the stored session has gone stale, so the next getSessionId call
// will mint a new one. Lets a caller record why a session ended without having
// to reimplement the expiry rules.
export function isSessionExpired(): boolean {
    const session = read();
    if (!session) return true;

    const now = Date.now();
    if (now >= session.expirationTimestamp) return true;

    const startedAt = session.startedAt ?? now;
    return now - startedAt >= ABSOLUTE_CAP_MS;
}
